import { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography
} from '@mui/material';

export interface BulkActionBarProps {
  /** Selectable rows currently shown — feeds the "all" confirm copy. */
  totalCount: number;
  /** Currently-selected row count. */
  selectedCount: number;
  /** True while a bulk request is in flight — disables every control. */
  busy?: boolean;
  /** Noun shown in the bar + dialog. Defaults to "market data". */
  noun?: string;
  /** Extra sentence appended to the disable-confirm dialog body. */
  disableWarning?: string;
  onEnableSelected: () => void;
  onDisableSelected: () => void;
  onClearSelection: () => void;
  onEnableAll: () => void;
  onDisableAll: () => void;
}

/**
 * Compact toolbar for bulk market-data ON/OFF, shared by the Opening and
 * Unwinding pages. It is CONTEXTUAL — one mode at a time:
 *  - no rows selected → master "All ON / All OFF" (server-side scope:'all').
 *  - rows selected   → "Enable / Disable" acting on just that subset
 *    (scope:'ids'), plus Clear.
 * Every "disable" path routes through a confirmation dialog — enabling never
 * needs one (arming a pair costs nothing, symmetric with the per-row switch).
 */
export function BulkActionBar({
  totalCount,
  selectedCount,
  busy = false,
  noun = 'market data',
  disableWarning,
  onEnableSelected,
  onDisableSelected,
  onClearSelection,
  onEnableAll,
  onDisableAll
}: BulkActionBarProps) {
  const [confirm, setConfirm] = useState<'selected' | 'all' | null>(null);

  const btnSx = { fontSize: '0.62rem', py: 0.1, px: 0.8, minWidth: 0 } as const;
  const labelSx = {
    fontSize: '0.62rem',
    color: 'grey.500',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
    whiteSpace: 'nowrap' as const
  };

  const hasSelection = selectedCount > 0;

  const confirmBody =
    confirm === 'all'
      ? `Disable ${noun} for ALL ~${totalCount} pair(s)? Active rules on them ` +
        'will be auto-paused and their Kalshi/Polymarket order books ' +
        'unsubscribed within ~5 minutes.'
      : `Disable ${noun} for ${selectedCount} selected pair(s)? Active rules ` +
        'on them will be auto-paused and their Kalshi/Polymarket order books ' +
        'unsubscribed within ~5 minutes.';

  const runConfirmed = () => {
    if (confirm === 'all') onDisableAll();
    else if (confirm === 'selected') onDisableSelected();
    setConfirm(null);
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.8,
          px: 1,
          py: 0.5,
          borderBottom: '1px solid rgba(255,255,255,0.08)'
        }}
      >
        {hasSelection ? (
          /* Selection mode — acts on the checked subset only (scope:'ids'). */
          <>
            <Typography sx={{ ...labelSx, color: 'grey.300' }}>
              {selectedCount} selected
            </Typography>
            <Button
              size="small"
              color="success"
              variant="outlined"
              sx={btnSx}
              disabled={busy}
              onClick={onEnableSelected}
            >
              Enable
            </Button>
            <Button
              size="small"
              color="error"
              variant="outlined"
              sx={btnSx}
              disabled={busy}
              onClick={() => setConfirm('selected')}
            >
              Disable
            </Button>
            <Button
              size="small"
              sx={{ ...btnSx, color: 'grey.500' }}
              disabled={busy}
              onClick={onClearSelection}
            >
              Clear
            </Button>
          </>
        ) : (
          /* Master mode — flips every eligible pair server-side (scope:'all'). */
          <>
            <Typography sx={labelSx}>All {noun}</Typography>
            <Button
              size="small"
              color="success"
              sx={btnSx}
              disabled={busy}
              onClick={onEnableAll}
            >
              All ON
            </Button>
            <Button
              size="small"
              color="error"
              sx={btnSx}
              disabled={busy}
              onClick={() => setConfirm('all')}
            >
              All OFF
            </Button>
          </>
        )}

        {busy && <CircularProgress size={12} />}
      </Box>

      <Dialog open={confirm !== null} onClose={() => setConfirm(null)}>
        <DialogTitle sx={{ fontSize: '0.95rem' }}>
          Disable {noun}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '0.85rem' }}>
            {confirmBody}
            {disableWarning ? ` ${disableWarning}` : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button color="error" onClick={runConfirmed} autoFocus>
            Disable
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
